interface PreservedState {
  timestamp: Date;
  data: Record<string, unknown>;
}

class StatePreserver {
  private readonly STORAGE_PREFIX = 'giorgie_preserved_';
  private readonly MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours
  private _storage?: Storage;

  // Lazy getter - evaluates localStorage at call time, not import time
  private get storage(): Storage {
    if (!this._storage) {
      this._storage = typeof window !== 'undefined' 
        ? window.localStorage 
        : this.createMemoryStorage(); // Fallback for SSR/tests
    }
    return this._storage;
  }

  // Allow injection for testing
  setStorage(storage: Storage): void {
    this._storage = storage;
  }

  // Reset for test isolation
  resetStorage(): void {
    this._storage = undefined;
  }

  private createMemoryStorage(): Storage {
    const store: Record<string, string> = {};
    return {
      getItem: (key: string) => store[key] || null,
      setItem: (key: string, value: string) => { store[key] = value; },
      removeItem: (key: string) => { delete store[key]; },
      clear: () => { Object.keys(store).forEach(k => delete store[k]); },
      key: (index: number) => Object.keys(store)[index] || null,
      get length() { return Object.keys(store).length; }
    };
  }

  checkpoint(key: string, data: Record<string, unknown>): void {
    try {
      const preserved: PreservedState = {
        timestamp: new Date(),
        data
      };
      
      this.storage.setItem(
        `${this.STORAGE_PREFIX}${key}`, 
        JSON.stringify(preserved)
      );
    } catch (error) {
      console.warn('Failed to preserve state:', error);
    }
  }

  recover(key: string): Record<string, unknown> | null {
    try {
      const stored = this.storage.getItem(`${this.STORAGE_PREFIX}${key}`);
      if (!stored) return null;

      const preserved: PreservedState = JSON.parse(stored);
      const age = Date.now() - new Date(preserved.timestamp).getTime();
      
      // Check if data is too old
      if (age > this.MAX_AGE_MS) {
        this.clearCheckpoint(key);
        return null;
      }

      return preserved.data;
    } catch (error) {
      console.warn('Failed to recover state:', error);
      return null;
    }
  }

  clearCheckpoint(key: string): void {
    try {
      this.storage.removeItem(`${this.STORAGE_PREFIX}${key}`);
    } catch (error) {
      console.warn('Failed to clear checkpoint:', error);
    }
  }

  hasCheckpoint(key: string): boolean {
    const recovered = this.recover(key);
    return recovered !== null;
  }

  listCheckpoints(): string[] {
    try {
      const keys: string[] = [];
      for (let i = 0; i < this.storage.length; i++) {
        const key = this.storage.key(i);
        if (key && key.startsWith(this.STORAGE_PREFIX)) {
          keys.push(key.replace(this.STORAGE_PREFIX, ''));
        }
      }
      return keys;
    } catch (error) {
      console.warn('Failed to list checkpoints:', error);
      return [];
    }
  }
}

// Singleton instance
export const statePreserver = new StatePreserver();